import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import * as React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplicationConfig } from '~/data/applicationConfig';
import { CompanyData } from '~/data/company';
import { EnumListKey } from '~/data/enums';
import {
  TEXT_LONG_MAX_LENGTH,
  TEXT_MAX_LENGTH,
} from '~/features/OverlappingEmploymentDialog/data';

import { useEnumFilteredOptions } from '../../hooks/useEnumFilteredOption';
import { OverlappingEmploymentFormValues } from '../../hooks/useOverlappingEmploymentForm';
import { useSxStyles } from '../../useStyles';

import { PersonnelGroupFormComponents } from './PersonneGroupFormComponents';

interface Props {
  formValues: OverlappingEmploymentFormValues;
  companyData: CompanyData;
}

export const EmploymentDetailTabForm = ({ formValues, companyData }: Props) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const { applicationConfig } = useApplicationConfig();
  const {
    formProps: { control },
  } = formValues;

  const { vacationCalculationDayOptions } = React.useMemo(() => {
    const is7DaysEnabled =
      applicationConfig?.fComConfig?.absConfig?.fComEnable7DaysVacationRule ??
      false;

    const vacationCalculationDayOptions = is7DaysEnabled
      ? ['5', '6', '7']
      : ['5', '6'];

    return {
      is7DaysEnabled,
      vacationCalculationDayOptions,
    };
  }, [applicationConfig]);

  const [filteredOptions] = useEnumFilteredOptions(control, [
    EnumListKey.JOB_GRADE,
  ]);

  return (
    <Box sx={sxStyles.container}>
      <Controller
        control={control}
        name={'overlappingEmployment.employmentDetails.fEmplDetailJobGrade'}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Autocomplete
            options={filteredOptions}
            getOptionLabel={(o) => o?.name || ''}
            isOptionEqualToValue={(o, v) => o?.code === v?.code}
            onChange={(_, value) => onChange(value?.code || '')}
            value={filteredOptions.find((o) => o?.code === value) || null}
            sx={{ ...sxStyles.selectStyle, ...sxStyles.inputBottomMargin }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('fJobGrade')}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        )}
      />

      <Controller
        control={control}
        name={'overlappingEmployment.employmentDetails.fEmplDetailTaxNumber'}
        rules={{
          maxLength: {
            value: TEXT_MAX_LENGTH,
            message: t('laMaxLengthRequired'),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            value={value}
            sx={{
              ...sxStyles.selectStyle,
              ...sxStyles.inputBottomMargin,
            }}
            onChange={onChange}
            label={t('fEmplDetailTaxNumber')}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name={'overlappingEmployment.employmentDetails.fEmplInfo'}
        rules={{
          maxLength: {
            value: TEXT_LONG_MAX_LENGTH,
            message: t('laMaxLengthRequired'),
          },
        }}
        render={({ field: { onChange, value } }) => (
          <TextField
            value={value}
            multiline
            rows={2}
            sx={{
              ...sxStyles.selectStyle,
              ...sxStyles.inputBottomMargin,
            }}
            onChange={onChange}
            label={t('fEmplInfo')}
          />
        )}
      />

      <Controller
        name={
          'overlappingEmployment.employmentDetails.fEmplDetailVacBalCalcSixDays'
        }
        control={control}
        render={({ field: { onChange, value } }) => {
          return (
            <FormControl>
              <FormLabel id="vacation-balance-calculation">
                {t('laVacBalCal')}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="vacation-balance-calculation"
                name="vac-bal-calc-radio-buttons-group"
                onChange={(event) => {
                  const value = parseInt(event.target.value);
                  if (!isNaN(value)) {
                    onChange(value.toString());
                  }
                }}
              >
                {vacationCalculationDayOptions.map((option) => {
                  const optionId = `idVacBalCal${option}`;
                  return (
                    <FormControlLabel
                      key={`balance-calculation-option-${option}`}
                      id={optionId}
                      value={option}
                      control={<Radio />}
                      label={option}
                      checked={value === option}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          );
        }}
      />

      <Controller
        name={
          'overlappingEmployment.employmentDetails.fEmplDetailCalcHoursBalance'
        }
        control={control}
        render={({ field: { onChange, value } }) => (
          <FormControlLabel
            label={t('laCalcHoursBalance')}
            sx={sxStyles.inputBottomMargin}
            control={
              <Checkbox
                checked={value}
                onChange={(e) => {
                  onChange(e.target.checked);
                }}
              />
            }
          />
        )}
      />

      <PersonnelGroupFormComponents
        companyData={companyData}
        control={control}
      />
    </Box>
  );
};
